import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { config } from '../config';
import { TokenEncryptionService } from './token-encryption.service';
import { getCountriesList } from '../countriesConfig';

export interface VerificationStatus {
  isVerified: boolean;
  phoneNumber: string | null;
  verifiedAt: string | null;
}

export interface CompanyVerificationContext {
  quotaStatus: string;
  isTrialCompany: boolean;
  isLocallyVerified: boolean;
  isMetaVerified: boolean;
  isPhoneVerified: boolean;
  shouldRequireVerification: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PhoneVerificationService {
  private verificationStatusSubject = new BehaviorSubject<VerificationStatus>({
    isVerified: false,
    phoneNumber: null,
    verifiedAt: null
  });

  public verificationStatus$ = this.verificationStatusSubject.asObservable();

  constructor(private http: HttpClient, private tokenEncryptionService: TokenEncryptionService) {
    this.loadVerificationStatus();
  }

  /**
   * Load verification status from localStorage
   */
  private loadVerificationStatus(): void {
    const status = this.tokenEncryptionService.getEncryptedItem<VerificationStatus>('phoneVerificationStatus');
    if (status) {
      this.verificationStatusSubject.next(status);
    }
  }

  /**
   * Save verification status to localStorage
   */
  private saveVerificationStatus(status: VerificationStatus): void {
    this.tokenEncryptionService.setEncryptedItem('phoneVerificationStatus', status);
    this.verificationStatusSubject.next(status);
  }

  /**
   * Check if the current user's phone is verified
   */
  isPhoneVerified(): boolean {
    return this.verificationStatusSubject.value.isVerified;
  }

  private normalizeQuotaStatus(status: any): string {
    return (status || '').toString().toLowerCase().trim();
  }

  private isMetaPhoneVerified(user: any): boolean {
    const meta = user?.meta || {};
    const hasPhone = meta.phone_number != null && meta.phone_number !== '';
    const verifiedFlag = meta.rao_phone_verified;
    const isVerifiedByFlag =
      verifiedFlag === '1' ||
      verifiedFlag === 1 ||
      verifiedFlag === true ||
      verifiedFlag === 'true';
    return hasPhone || isVerifiedByFlag;
  }

  getCompanyVerificationContext(user?: any): CompanyVerificationContext {
    const sourceUser = user || this.tokenEncryptionService.getEncryptedItem<any>('user') || {};
    const quotaStatus = this.normalizeQuotaStatus(sourceUser?.meta?.quota_status);
    const isTrialCompany = quotaStatus === 'trial';
    const isLocallyVerified = this.isPhoneVerified();
    const isMetaVerified = this.isMetaPhoneVerified(sourceUser);
    const isPhoneVerified = isLocallyVerified || isMetaVerified;
    // Product flows (add employee, import, settings) must not hard-block on phone OTP.
    // Billing checkout keeps its own gate in user-plans.
    const shouldRequireVerification = false;

    return {
      quotaStatus,
      isTrialCompany,
      isLocallyVerified,
      isMetaVerified,
      isPhoneVerified,
      shouldRequireVerification
    };
  }

  shouldRequirePhoneVerificationForSettings(user?: any): boolean {
    return this.getCompanyVerificationContext(user).shouldRequireVerification;
  }

  syncVerificationStatusFromUserMeta(user: any): void {
    const meta = user?.meta || {};
    const phoneNumber = meta.phone_number;
    const verifiedFlag = meta.rao_phone_verified;
    const isVerifiedByFlag =
      verifiedFlag === '1' ||
      verifiedFlag === 1 ||
      verifiedFlag === true ||
      verifiedFlag === 'true';

    if (phoneNumber || isVerifiedByFlag) {
      this.saveVerificationStatus({
        isVerified: true,
        phoneNumber: phoneNumber || this.verificationStatusSubject.value.phoneNumber || null,
        verifiedAt: this.verificationStatusSubject.value.verifiedAt || new Date().toISOString()
      });
      return;
    }

    this.saveVerificationStatus({
      isVerified: false,
      phoneNumber: null,
      verifiedAt: null
    });
  }


  setVerificationStatus(phoneNumber: string): void {
    if(phoneNumber && phoneNumber.length > 5){
      this.verificationStatusSubject.next({
        isVerified: true,
        phoneNumber: phoneNumber,
        verifiedAt: new Date().toISOString()
      });
    } else {
      this.verificationStatusSubject.next({
        isVerified: false,
        phoneNumber: null,
        verifiedAt: null
      });
    }
  }

  /**
   * Get current verification status
   */
  getVerificationStatus(): VerificationStatus {
    return this.verificationStatusSubject.value;
  }

  /**
   * Send verification code to phone number via SMS or WhatsApp
   * @param phoneNumber - Phone number in E.164 format (e.g., +1234567890)
   * @param channel - 'sms' or 'whatsapp'
   */
  sendVerificationCode(phoneNumber: string, channel: 'sms' | 'whatsapp' = 'sms', countryCode: string, dialCode: string): Observable<any> {
    console.log(`[PhoneVerification] Sending ${channel} verification code to ${phoneNumber}`);
    
    const phoneData = {
      country_iso2: countryCode,
      dial_code: dialCode,
      national_number: phoneNumber.replace(dialCode, '')
    }
    
    // Call backend endpoint to send verification code via Twilio
    return this.http.post(`${config.baseUrl}rao/phone/otp/send`, {
      country_iso2: phoneData.country_iso2,
      dial_code: phoneData.dial_code,
      national_number: phoneData.national_number
    }).pipe(
      tap((response: any) => {
        console.log('[PhoneVerification] Code sent successfully', response);
      }),
      catchError((error) => {
        console.error('[PhoneVerification] Failed to send code', error);
        throw error;
      })
    );
  }

  /**
   * Parse phone number to extract country ISO2, dial code, and national number
   * @param fullPhoneNumber - Full phone number with country code (e.g., +919876543210)
   */
  private parsePhoneNumber(fullPhoneNumber: string): { country_iso2: string, dial_code: string, national_number: string } {
    // Get full countries list from config
    const countries = getCountriesList();
    
    // Remove any spaces or special characters
    const cleaned = fullPhoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Normalize dial codes: remove dashes and ensure they start with +
    const normalizedCountries = countries.map(country => ({
      ...country,
      normalizedDialCode: country.dialCode.replace(/-/g, '').replace(/^\+?/, '+')
    }));
    
    // Sort by dial code length (longest first) to match longer codes before shorter ones
    // This prevents +1 from matching before +1234
    const sortedCountries = normalizedCountries.sort((a, b) => {
      const lenA = a.normalizedDialCode.length;
      const lenB = b.normalizedDialCode.length;
      if (lenA !== lenB) {
        return lenB - lenA; // Longer codes first
      }
      return a.normalizedDialCode.localeCompare(b.normalizedDialCode);
    });
    
    // Try to match with country codes
    for (const country of sortedCountries) {
      if (cleaned.startsWith(country.normalizedDialCode)) {
        return {
          country_iso2: country.code,
          dial_code: country.normalizedDialCode,
          national_number: cleaned.substring(country.normalizedDialCode.length)
        };
      }
    }
    
    // Default to US if no match (should rarely happen with full country list)
    console.warn(`[PhoneVerification] Could not parse country code from phone number: ${fullPhoneNumber}, defaulting to US`);
    return {
      country_iso2: 'US',
      dial_code: '+1',
      national_number: cleaned.replace(/^\+/, '')
    };
  }

  /**
   * Verify the code entered by the user
   * @param phoneNumber - Phone number in E.164 format
   * @param code - 6-digit verification code
   */
  verifyCode(phoneNumber: string, code: string): Observable<any> {
    console.log(`[PhoneVerification] Verifying code for ${phoneNumber}`);
    
    // Parse phone number to extract country code and national number
    const phoneData = this.parsePhoneNumber(phoneNumber);
    
    return this.http.post(`${config.baseUrl}rao/phone/otp/verify`, {
      country_iso2: phoneData.country_iso2,
      dial_code: phoneData.dial_code,
      national_number: phoneData.national_number,
      code: code
    }).pipe(
      map((response: any) => {
        console.log('[PhoneVerification] Verify response:', response);
        
        // Check if there's an error code in the response (API returns 200 but with error payload)
        if (response.code && response.code.includes('failed')) {
          console.error('[PhoneVerification] Verification failed with error code:', response.code);
          throw {
            error: {
              code: response.code,
              message: response.message || response.data?.message || 'Verification failed. The code is invalid or expired.',
              data: response.data
            }
          };
        }
        
        // Check if verification was successful
        const isVerified = response.verified === true || 
                          response.data?.verified === true || 
                          response.status === 'verified' || 
                          response.status === 'approved' ||
                          response.data?.status === 'approved';
        
        if (isVerified) {
          console.log('[PhoneVerification] Code verified successfully');
          this.markAsVerified(phoneNumber);
          return response;
        } else {
          // Verification failed or pending
          console.error('[PhoneVerification] Verification failed:', response);
          throw {
            error: {
              code: response.code || 'invalid_code',
              message: response.message || response.data?.message || 'Invalid verification code. Please try again.',
              data: response.data || response
            }
          };
        }
      }),
      catchError((error) => {
        console.error('[PhoneVerification] Failed to verify code', error);
        throw error;
      })
    );
  }

  /**
   * Mark the phone number as verified
   */
  markAsVerified(phoneNumber: string): void {
    const status: VerificationStatus = {
      isVerified: true,
      phoneNumber: phoneNumber,
      verifiedAt: new Date().toISOString()
    };
    this.saveVerificationStatus(status);
    console.log('[PhoneVerification] Phone marked as verified');
  }

  /**
   * Clear verification status (for logout or when needed)
   */
  clearVerificationStatus(): void {
    const status: VerificationStatus = {
      isVerified: false,
      phoneNumber: null,
      verifiedAt: null
    };
    this.saveVerificationStatus(status);
    console.log('[PhoneVerification] Verification status cleared');
  }

  /**
   * Reset verification status for a new session
   */
  resetVerification(): void {
    this.clearVerificationStatus();
  }

  /**
   * Update user's phone number in the backend
   * @param userId - User ID
   * @param phoneNumber - Phone number to update
   */
  updateUserPhoneNumber(userId: number, phoneNumber: string): Observable<any> {
    return this.http.post(`${config.baseUrl}wp/v2/users/${userId}`, {
      meta: {
        phone_number: phoneNumber
      }
    }).pipe(
      tap(() => {
        console.log('[PhoneVerification] User phone number updated');
      }),
      catchError((error) => {
        console.error('[PhoneVerification] Failed to update phone number', error);
        throw error;
      })
    );
  }
}

